import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import pixelService from "./services/pixelService";
import UserAgentGuard from "./components/UserAgentGuard";
import useTheme from "./hooks/useTheme";
import useSiteInfo from "./hooks/useSiteInfo";
import useUtmify from "./hooks/useUtmify";
import { CartProvider } from "./context/CartContext";
import CookieConsent from "./components/CookieConsent";
import SocialProofToast from "./components/SocialProofToast";
import LeadCapturePopup from "./components/LeadCapturePopup";
import PresenceTracker from "./components/PresenceTracker";

const HomePage = lazy(() => import("./pages/HomePage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutRouter"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const MeusPedidosPage = lazy(() => import("./pages/MeusPedidosPage"));
const RastreioPage = lazy(() => import("./pages/RastreioPage"));
const PayOrderPage = lazy(() => import("./pages/PayOrderPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));

const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: '#F5F5F5'
  }}>
    <div style={{
      width: 36,
      height: 36,
      border: '3px solid #E5E7EB',
      borderTopColor: '#0047FF',
      borderRadius: '50%',
      animation: 'spin 0.6s linear infinite'
    }} />
  </div>
);

function App() {
  // Detect in-app browser early
  const isInApp = /TikTok|Instagram|FBAN|FBAV|Twitter|Line|Snapchat/i.test(navigator.userAgent || '');

  useEffect(() => {
    // Delay pixel scripts more for in-app browsers
    const delay = isInApp ? 5000 : 2000;
    const timer = setTimeout(() => {
      pixelService.init();
    }, delay);
    return () => clearTimeout(timer);
  }, [isInApp]);

  // These hooks make API calls - skip non-essential for in-app
  useTheme();
  useSiteInfo();
  // Delay UTMify even more (it injects back-redirect that breaks in-app navigation)
  useUtmify();

  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<UserAgentGuard><HomePage /></UserAgentGuard>} />
              <Route path="/carrinho" element={<CartPage />} />
              <Route path="/checkout" element={<UserAgentGuard><CheckoutPage /></UserAgentGuard>} />
              <Route path="/checkout/:productId" element={<UserAgentGuard><CheckoutPage /></UserAgentGuard>} />
              <Route path="/produto/:productId" element={<UserAgentGuard><ProductPage /></UserAgentGuard>} />
              <Route path="/privacidade" element={<PrivacyPolicyPage />} />
              <Route path="/trocas-devolucoes" element={<ReturnsPage />} />
              <Route path="/termos-de-uso" element={<TermsPage />} />
              <Route path="/meus-pedidos" element={<MeusPedidosPage />} />
              <Route path="/rastreio" element={<RastreioPage />} />
              <Route path="/pagar/:orderId" element={<PayOrderPage />} />
              <Route path="/sobre" element={<AboutPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
              <Route path="/painel1" element={<AdminPage />} />
            </Routes>
          </Suspense>
          <CookieConsent />
          <SocialProofToast />
          <LeadCapturePopup />
          <PresenceTracker />
        </BrowserRouter>
      </CartProvider>
    </div>
  );
}

export default App;
